// @core/stores/subscription/SubscriptionStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { subscriptionApi } from '@core/api';
import type {
  CreateFeatureUsageRecordRequest,
  CreateFeatureUsageRecordResponse,
  CreateSubscriptionItemRequest,
  CreateSubscriptionRequest,
  GetFeatureUsageResponse,
  GetSubscriptionPlanChangePreviewResponse,
  ListProductTenantsResponse,
  ListSubscriptionItemsResponse,
  ProductTenantResponse,
  PurchaseAddOnRequest,
  PurchaseAddOnResponse,
  SetSubscriptionCancelRequest,
  SetSubscriptionPauseRequest,
  SetSubscriptionPlanChangeRequest,
  SetSubscriptionPlanChangeResponse,
  SetSubscriptionResumeRequest,
  SubscribeTenantRequest,
  Subscription,
  SubscriptionItemResponse,
  SubscriptionResponse,
  UpdateSubscriptionItemRequest,
  UpdateSubscriptionRequest,
  UpdateTenantSubscriptionRequest,
} from '@core/types/subscription';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class SubscriptionStore {
  // List state
  items: Subscription[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: Subscription | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, Subscription>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): Subscription | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: Subscription | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }
  private removeFromCache(id: string): void {
    this.cache.delete(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Cancel tenant subscription
   */
  async CancelTenantSubscription(tenant_id: string, product_id: string): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await subscriptionApi.CancelTenantSubscription(tenant_id, product_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Record feature usage
   */
  async CreateFeatureUsageRecord(subscription_id: string, data: CreateFeatureUsageRecordRequest): Promise<CreateFeatureUsageRecordResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await subscriptionApi.CreateFeatureUsageRecord(subscription_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create a new subscription
   */
  async CreateSubscription(data: CreateSubscriptionRequest): Promise<Subscription> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await subscriptionApi.CreateSubscription(data);
      const item = response.subscription;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Add item to subscription
   */
  async CreateSubscriptionItem(subscription_id: string, data: CreateSubscriptionItemRequest): Promise<SubscriptionItemResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await subscriptionApi.CreateSubscriptionItem(subscription_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Delete subscription
   */
  async DeleteSubscription(subscription_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await subscriptionApi.DeleteSubscription(subscription_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== subscription_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === subscription_id) {
          this.currentItem = null;
        }

        this.removeFromCache(subscription_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Remove subscription item
   */
  async DeleteSubscriptionItem(subscription_id: string, item_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await subscriptionApi.DeleteSubscriptionItem(subscription_id, item_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== item_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === item_id) {
          this.currentItem = null;
        }

        this.removeFromCache(item_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Get feature usage
   */
  async GetFeatureUsage(subscription_id: string): Promise<GetFeatureUsageResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await subscriptionApi.GetFeatureUsage(subscription_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get subscription details
   */
  async GetSubscription(subscription_id: string): Promise<Subscription> {
    // Check cache first
    const cached = this.cache.get(subscription_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await subscriptionApi.GetSubscription(subscription_id);
      const item = response.subscription;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * Get subscription item
   */
  async GetSubscriptionItem(subscription_id: string, item_id: string): Promise<SubscriptionItemResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await subscriptionApi.GetSubscriptionItem(subscription_id, item_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Preview subscription plan change
   */
  async GetSubscriptionPlanChangePreview(subscription_id: string, params?: { new_plan_id?: string | number | boolean; prorate?: string | number | boolean; }): Promise<GetSubscriptionPlanChangePreviewResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await subscriptionApi.GetSubscriptionPlanChangePreview(subscription_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get tenant subscription to product
   */
  async GetTenantSubscription(tenant_id: string, product_id: string): Promise<ProductTenantResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await subscriptionApi.GetTenantSubscription(tenant_id, product_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List subscription items
   */
  async ListSubscriptionItems(subscription_id: string, params?: PaginatedRequest): Promise<ListSubscriptionItemsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await subscriptionApi.ListSubscriptionItems(subscription_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List subscriptions
   */
  async ListSubscriptions(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await subscriptionApi.ListSubscriptions(params);

      runInAction(() => {
        this.items = response.subscriptions?.items ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List tenant's product subscriptions
   */
  async ListTenantSubscriptions(tenant_id: string, params?: PaginatedRequest): Promise<ListProductTenantsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await subscriptionApi.ListTenantSubscriptions(tenant_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Purchase addon
   */
  async PurchaseAddOn(subscription_id: string, data: PurchaseAddOnRequest): Promise<PurchaseAddOnResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await subscriptionApi.PurchaseAddOn(subscription_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Cancel subscription
   */
  async SetSubscriptionCancel(subscription_id: string, data: SetSubscriptionCancelRequest): Promise<SubscriptionResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await subscriptionApi.SetSubscriptionCancel(subscription_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Pause subscription
   */
  async SetSubscriptionPause(subscription_id: string, data: SetSubscriptionPauseRequest): Promise<SubscriptionResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await subscriptionApi.SetSubscriptionPause(subscription_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Change subscription plan
   */
  async SetSubscriptionPlanChange(subscription_id: string, data: SetSubscriptionPlanChangeRequest): Promise<SetSubscriptionPlanChangeResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await subscriptionApi.SetSubscriptionPlanChange(subscription_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Resume subscription
   */
  async SetSubscriptionResume(subscription_id: string, data: SetSubscriptionResumeRequest): Promise<SubscriptionResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await subscriptionApi.SetSubscriptionResume(subscription_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Subscribe tenant to product
   */
  async SubscribeTenant(tenant_id: string, product_id: string, data: SubscribeTenantRequest): Promise<ProductTenantResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await subscriptionApi.SubscribeTenant(tenant_id, product_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update subscription
   */
  async UpdateSubscription(subscription_id: string, data: UpdateSubscriptionRequest): Promise<Subscription> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await subscriptionApi.UpdateSubscription(subscription_id, data);
      const item = response.subscription;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Update subscription item
   */
  async UpdateSubscriptionItem(subscription_id: string, item_id: string, data: UpdateSubscriptionItemRequest): Promise<SubscriptionItemResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await subscriptionApi.UpdateSubscriptionItem(subscription_id, item_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update tenant subscription
   */
  async UpdateTenantSubscription(tenant_id: string, product_id: string, data: UpdateTenantSubscriptionRequest): Promise<ProductTenantResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await subscriptionApi.UpdateTenantSubscription(tenant_id, product_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }
}

export const subscriptionStore = new SubscriptionStore();
